// This is loosely copied and cleaned from https://github.com/plotly/dash-bio/blob/master/src/lib/fragments/NeedlePlot.react.js
// We've made edits, such as more prop-driven configuration in parts.
import {
  createHorizontalLine,
  extractSmallDomains,
  nanMax,
  getVariantType,
} from './utils';
import { makeStyles, useTheme } from '@lifeomic/chroma-react/styles';
import * as React from 'react';
import clsx from 'clsx';
import Plot from 'react-plotly.js';
import useWindowSize from '@lifeomic/chroma-react/hooks/events/useWindowSize';

const useStyles = makeStyles(_theme => ({
  root: {
    width: '100%',
  },
}));

// Fetch layout
const prepareLayout = ({
  data,
  globalAnnotation,
  domainAnnotations,
  xLabel,
  yLabel,
  rangeSlider,
  width,
  showYaxis,
  autoSize,
}) => {
  let first_init = false;
  let xStart = null;
  let xEnd = null;

  // initialize the range based on input data
  if (Boolean(!xStart) || Boolean(!xEnd)) {
    first_init = true;
    data.forEach(trace => {
      const X_DATA_MIN = Math.min.apply(null, trace.x);
      const X_DATA_MAX = Math.max.apply(null, trace.x);
      if (xStart > X_DATA_MIN || Boolean(!xStart)) {
        xStart = X_DATA_MIN;
      }
      if (xEnd < X_DATA_MAX || Boolean(!xEnd)) {
        xEnd = X_DATA_MAX;
      }
    });
  }

  // this is used to zoom in the axis range initially
  const XSTART_RATIO = 0.98;
  const XEND_RATIO = 1.02;

  const layout = {
    height: 400,
    width: width ? width - 100 : '100%',
    legend: {
      orientation: 'v',
      x: 1,
      y: 1.05,
      bgcolor: 'rgba(255, 255, 255, 0)',
    },
    hovermode: 'closest',
    xaxis: {
      title: xLabel,
      showgrid: false,
      zeroline: false,
      autorange: Boolean(!xStart),
      range: [-5, xEnd + 5],
      anchor: 'y',
    },
    xaxis2: {
      scaleanchor: 'x',
      autorange: Boolean(!xStart),
      range: [-5, xEnd + 5],
      anchor: 'y',
      overlaying: 'x',
      zeroline: false,
    },
    yaxis: {
      title: yLabel,
      showgrid: false,
      ticks: '',
      visible: showYaxis,
      showticklabels: false,
    },
    margin: { t: 100, l: 40, r: 40, b: 40 },
    annotations: domainAnnotations.concat(globalAnnotation),
  };
  if (rangeSlider === true) {
    layout.xaxis.rangeslider =
      first_init === true
        ? { range: [xStart * XSTART_RATIO, xEnd * XEND_RATIO] }
        : {};
  }

  if (autoSize) {
    delete layout.height;
    delete layout.width;
    layout.autosize = true;
  }

  return layout;
};

const prepareTraces = ({
  mutationData: {
    x,
    y,
    mutationGroups,
    domains,
    needleHoverPointText,
    primary,
  },
  domainStyle: { displayMinorDomains },
  showNeedlePinLegend,
  showDomainLegend,
  colorOptions,
  showHoverLabel,
}) => {
  // Apply filtering on protein positions
  const [x_single_site, small_domains, idx_old_positions_array] =
    extractSmallDomains(x);

  const X_DATA_MIN = Math.min.apply(null, x_single_site);
  const X_DATA_MAX = Math.max.apply(null, x_single_site);
  const Y_DATA_MAX = nanMax(y);
  const X_RANGE_MIN = X_DATA_MIN; // TODO: xStart maybe?
  const X_RANGE_MAX = X_DATA_MAX; // TODO: xEnd maybe?
  const XSPAN = X_RANGE_MAX - X_RANGE_MIN;
  // this is used to trigger a change of display inside annotations
  const XSPAN_RATIO = 0.2;
  const Y_BUFFER = Y_DATA_MAX / 10;
  // this is used to scale the position for the annotations
  const Y_BUFFER_DIVIDER = 2;
  const Y_TOP = Y_DATA_MAX + Y_BUFFER;
  const DOMAIN_WIDTH = 33;

  const sequenceDomains = [];
  const domainAnnotations = [];

  let hoverlabels = needleHoverPointText;
  // contains the height of each stem
  let stemsY = [];
  idx_old_positions_array.forEach(idx => {
    if (needleHoverPointText.length === 0) {
      hoverlabels = hoverlabels.concat(['(' + x[idx] + ',' + y[idx] + ')']);
    }
    stemsY = stemsY.concat([y[idx]]);
  });

  // build the different protein large domains
  domains.forEach((dom, i) => {
    const domainLimits = dom.coord.split('-');
    const x0 = Number(domainLimits[0]);
    const x1 = Number(domainLimits[1]);
    const domainLength = x1 - x0;

    // Highlight of the protein domain
    sequenceDomains.push({
      x: [x1, x0],
      y: [Y_TOP, Y_TOP],
      xaxis: 'x1',
      name: dom.name,
      fill: 'tozeroy',
      mode: 'lines',
      opacity: 0.5,
      visible: 'legendonly',
      legendgroup: dom.name,
      marker: { color: dom.color },
      showlegend: showDomainLegend,
    });
    const [line_x, line_y] = createHorizontalLine(x0, x1, -Y_BUFFER, x1 - x0);

    sequenceDomains.push({
      type: 'scatter',
      mode: 'lines',
      fill: 'tozeroy',
      fillcolor: dom.color,
      hoveron: 'points+fills',
      x: line_x,
      y: line_y,
      xaxis: 'x2',
      showlegend: false,
      hovertemplate: showHoverLabel ? `${dom.fullName} (${x0}->${x1})` : '',
      name: '',
      marker: { color: dom.color },
      line: { width: 2 },
    });

    // Name of the protein domain
    domainAnnotations.push({
      x: (x0 + x1) / Y_BUFFER_DIVIDER,
      y: -Y_BUFFER / Y_BUFFER_DIVIDER,
      showarrow: false,
      text: dom.name,
      width: domainLength,
      align: domainLength < XSPAN_RATIO * XSPAN ? 'right' : 'center',
    });
  });

  if (displayMinorDomains === true) {
    // build the different protein small domains
    small_domains.forEach(dom => {
      const x0 = Number(dom.split('-')[0]);
      const x1 = Number(dom.split('-')[1]);
      const gname = mutationGroups[x.indexOf(dom)];
      const [line_x, line_y] = createHorizontalLine(
        x0,
        x1,
        -Y_BUFFER / Y_BUFFER_DIVIDER,
        x1 - x0,
      );
      // Range of the protein domain on the xaxis
      sequenceDomains.push({
        type: 'scatter',
        mode: 'lines',
        x: line_x,
        y: line_y,
        fill: 'tozeroy',
        fillcolor: colorOptions.black,
        hoveron: 'points+fills',
        xaxis: 'x2',
        hoverinfo: showHoverLabel ? 'name+text' : 'none',
        name: gname,
        text: `[${x0}->${x1}] `,
        showlegend: false,
        marker: {
          color: colorOptions.black,
        },
        line: { width: DOMAIN_WIDTH },
      });
    });
  }

  const mappedGroups = mutationGroups.map(g => getVariantType(g).legend);
  const styles = [...new Set(mutationGroups)]
    .filter(t => !!t)
    .map(target => {
      return {
        target: getVariantType(target).legend,
        value: {
          marker: {
            size: 10,
            symbol: 'circle',
            color: getVariantType(target).color,
          },
        },
      };
    });

  const data = [
    {
      type: 'scatter',
      mode: 'markers',
      x: x_single_site,
      y: stemsY,
      xaxis: 'x1',
      hoverinfo: showHoverLabel ? 'name+text' : 'none',
      text: showHoverLabel ? hoverlabels : '',
      showlegend: showNeedlePinLegend,
      error_y: {
        type: 'data',
        symmetric: false,
        array: 0,
        arrayminus: stemsY,
        thickness: 0.5,
        width: 0,
        color: '#444',
      },
      transforms: [
        {
          type: 'groupby',
          groups: mappedGroups,
          nameformat: `%{group}`,
          styles,
        },
      ],
    },
  ].concat(sequenceDomains);

  const globalAnnotation = [];

  if (primary) {
    const maxY = Math.max(...stemsY);
    data.push({
      name: primary.name,
      hoverinfo: showHoverLabel ? 'text' : 'none',
      hovertext: primary.hovertext,
      type: 'bar',
      x: [primary.x],
      y: [maxY + 1],
      width: 1,
      showlegend: false,
      marker: {
        color: getVariantType(primary.transcriptClass).color,
      },
    });

    globalAnnotation.push({
      x: primary.x,
      y: maxY + 0.5,
      xref: 'x',
      yref: 'y',
      text: primary.name,
      showarrow: true,
      arrowhead: 3,
      arrowcolor: getVariantType(primary.transcriptClass).color,
      ax: X_DATA_MAX - primary.x < 100 ? -130 : 130,
      ay: -20,
      font: {
        size: 14,
        color: '#ffffff',
      },
      bgcolor: getVariantType(primary.transcriptClass).color,
      opacity: 0.8,
    });
  }

  return { data, globalAnnotation, domainAnnotations };
};

export const NeedlePlot = ({
  className,
  config,
  mutationData,
  rangeSlider,
  domainStyle,
  showNeedlePinLegend = true,
  showDomainLegend = true,
  showYaxis = true,
  xLabel = '',
  yLabel = '',
  autoSize = false,
  showHoverLabel = true,
  ...rootProps
}) => {
  const classes = useStyles({});
  const { palette } = useTheme();

  const { width } = useWindowSize();

  const { data, globalAnnotation, domainAnnotations } = prepareTraces({
    mutationData,
    rangeSlider,
    domainStyle,
    showNeedlePinLegend,
    showDomainLegend,
    colorOptions: {
      black: palette.black.main,
    },
    showHoverLabel,
  });

  const layout = prepareLayout({
    data,
    globalAnnotation,
    domainAnnotations,
    rangeSlider,
    xLabel,
    yLabel,
    width,
    showYaxis,
    autoSize,
  });

  return (
    <Plot
      className={clsx(classes.root, className)}
      config={config}
      data={data}
      layout={layout}
      {...rootProps}
    />
  );
};
